import { Switch } from "@xola/ui-kit";
import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import { UserDetailsRequirement } from "types";
import shallow from "zustand/shallow";
import { CheckoutFormValues } from "./CheckoutPurchaseScreen.types";
import { FormValues as GiftFormValues } from "./CreatePurchasePersonalizeGiftFields";
import { useGiftDetailsRequirements } from "./hooks/useGiftDetailsRequirements";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { getOnlyGiftWithRecipient, getRecipientRules } from "@/models/GiftPreference";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

export const SameAsRecipientToggle = () => {
    const { values, setFieldValue } = useFormikContext<CheckoutFormValues & Partial<GiftFormValues>>();
    const { recipientDetailsRequirement, organizerDetailsRequirement } = useGiftDetailsRequirements();
    const [hasRecipientGift, isRecipientCollected, recipientName, recipientEmail] = useCreatePurchaseStore((state) => {
        const gift = getOnlyGiftWithRecipient(state.purchase.items, recipientDetailsRequirement);
        return [
            !!gift,
            !!gift && getRecipientRules(recipientDetailsRequirement, gift).isRecipientCollected,
            gift?.recipientName ?? "",
            gift?.recipientEmail ?? "",
        ];
    }, shallow);
    const hasOrganizer = useCreatePurchaseStore((state) => !!state.organizer);
    const isSameAsRecipient = useCustomerDetailsStore((state) => state.isSameAsRecipient);
    const isEnabled = isRecipientCollected && !hasOrganizer;

    useEffect(() => {
        if (isSameAsRecipient && !isEnabled) {
            useCustomerDetailsStore.setState({ isSameAsRecipient: false });
        }
    }, [isSameAsRecipient, isEnabled]);

    const handleChange = (isChecked: boolean) => {
        useCustomerDetailsStore.setState({ isSameAsRecipient: isChecked });
        if (isChecked) {
            // The form holds what was just typed; the store copy of the recipient is debounced.
            const customerName = values.recipientName ?? recipientName;
            const customerEmail = values.recipientEmail ?? recipientEmail;
            setFieldValue("customerName", customerName, false);
            setFieldValue("customerEmail", customerEmail, false);
            useCustomerDetailsStore.setState({ customerName, customerEmail });
        }
        if (isChecked && organizerDetailsRequirement === UserDetailsRequirement.OPTIONAL_CONFIRMATION) {
            useCustomerDetailsStore.setState({ nameRequired: true, emailRequired: true });
        }
    };

    if (!hasRecipientGift) {
        return null;
    }

    return (
        <Switch.Group>
            <Switch
                size="small"
                isChecked={isSameAsRecipient}
                disabled={!isEnabled}
                data-testid="same-as-recipient-toggle"
                onChange={handleChange}
            />
            <Switch.Label className="ml-2 text-gray-darker" direction="left">
                Same as recipient
            </Switch.Label>
        </Switch.Group>
    );
};
